import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import type { CreateTimeOffRequestBody } from "../../utils/models";

/** Default effective_from cutoff: start of the current ISO week (Monday). Past requests are excluded. */
const getCurrentWeekStartDate = () => DateTime.now().startOf("week");

const myTimeOffRequestsKey = "my-time-off-requests";
const departmentTimeOffRequestsKey = "department-time-off-requests";

export const useGetMyTimeOffRequests = () => {
	const { track } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	return useQuery({
		queryKey: [myTimeOffRequestsKey],
		queryFn: async () => {
			try {
				const response = await backendService.getTimeOffRequests();
				track("fetched_time_off_requests", { count: response.data.length });
				return response.data;
			} catch (error) {
				showErrorSnackbar("Failed to fetch time off requests.", {}, error);
				return [];
			}
		},
		staleTime: 10 * 1000, // 10 seconds
		throwOnError: false,
	});
};

export const useGetDepartmentTimeOffRequests = ({ includeAllStatuses = false }: { includeAllStatuses?: boolean } = {}) => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	// Filter out requests that ended before this week — no consumer needs them.
	const effectiveFrom = getCurrentWeekStartDate();
	return useQuery({
		queryKey: [departmentTimeOffRequestsKey, chosenDepartment?.department_id, includeAllStatuses, effectiveFrom],
		queryFn: async () => {
			if (!chosenDepartment?.department_id) {
				return [];
			}
			try {
				const response = await backendService.getDepartmentTimeOffRequests(chosenDepartment.department_id, includeAllStatuses, effectiveFrom);
				return response.data;
			} catch (error) {
				showErrorSnackbar("Failed to fetch department time off requests.", {}, error);
				return [];
			}
		},
		staleTime: 10 * 1000, // 10 seconds
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id,
	});
};

export const useCreateTimeOffRequest = () => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	return useMutation({
		mutationFn: async (timeOffRequestData: CreateTimeOffRequestBody) => {
			track("create_time_off_request", { departmentId: chosenDepartment?.department_id, request: JSON.stringify(timeOffRequestData) });
			const response = await backendService.createTimeOffRequest({
				departmentId: chosenDepartment?.department_id,
				body: timeOffRequestData,
			});
			return response.data;
		},
		throwOnError: false,
		onSuccess: async (data) => {
			track("created_time_off_request", { departmentId: chosenDepartment?.department_id, requestId: data.id });
			showSnackbar("Time off request created successfully.", "success");
			await queryClient.invalidateQueries({ queryKey: [myTimeOffRequestsKey] });
		},
		onError: (error, variables) => {
			showErrorSnackbar("Failed to create time off request. Please try again.", { variables }, error);
		},
	});
};

export const useCancelTimeOffRequest = () => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	return useMutation({
		mutationFn: async (requestId: string) => {
			track("cancel_time_off_request", { departmentId: chosenDepartment?.department_id, request: JSON.stringify(requestId) });
			const response = await backendService.cancelTimeOffRequest(requestId);
			return response.data;
		},
		throwOnError: false,
		onSuccess: async (data) => {
			track("canceled_time_off_request", { departmentId: chosenDepartment?.department_id, requestId: data.id });
			showSnackbar("Time off request canceled.", "info");
			await queryClient.invalidateQueries({ queryKey: [myTimeOffRequestsKey] });
		},
		onError: (error, variables) => {
			showErrorSnackbar("Failed to cancel time off request. Please try again.", { variables }, error);
		},
	});
};

export const useApproveTimeOffRequest = () => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	return useMutation({
		mutationFn: async (requestId: string) => {
			if (!chosenDepartment?.department_id) {
				throw new Error("No department selected");
			}
			track("approve_time_off_request", { departmentId: chosenDepartment.department_id, requestId });
			const response = await backendService.approveTimeOffRequest(requestId, chosenDepartment.department_id);
			return response.data;
		},
		throwOnError: false,
		onSuccess: async (data) => {
			track("approved_time_off_request", { departmentId: chosenDepartment?.department_id, requestId: data.id });
			showSnackbar("Time off request approved.", "success");
			await queryClient.invalidateQueries({ queryKey: [departmentTimeOffRequestsKey] });
		},
		onError: (error, variables) => {
			showErrorSnackbar("Failed to approve time off request. Please try again.", { variables }, error);
		},
	});
};

export const useDeclineTimeOffRequest = () => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	return useMutation({
		mutationFn: async (requestId: string) => {
			if (!chosenDepartment?.department_id) {
				throw new Error("No department selected");
			}
			track("decline_time_off_request", { departmentId: chosenDepartment.department_id, requestId });
			const response = await backendService.declineTimeOffRequest(requestId, chosenDepartment.department_id);
			return response.data;
		},
		throwOnError: false,
		onSuccess: async (data) => {
			track("declined_time_off_request", { departmentId: chosenDepartment?.department_id, requestId: data.id });
			showSnackbar("Time off request declined.", "info");
			await queryClient.invalidateQueries({ queryKey: [departmentTimeOffRequestsKey] });
		},
		onError: (error, variables) => {
			showErrorSnackbar("Failed to decline time off request. Please try again.", { variables }, error);
		},
	});
};
